import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AnimatePresence } from 'motion/react';
import AppShell from '../components/work/AppShell';
import WorkItemTableRow from '../components/work/WorkItemTableRow';
import FilterBar from '../components/work/FilterBar';
import Peek from '../components/work/Peek';
import BulkBar, { type BulkSelection } from '../components/work/BulkBar';
import ItemContextMenu from '../components/work/ItemContextMenu';
import { Checkbox } from '../components/ui/checkbox';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { MoreHorizontal, Plus } from 'lucide-react';
import WorkKanban from '../components/work/WorkKanban';
import { BlockedBadge } from '../components/work/WorkCardMeta';
import NewWorkItemMenu from '../components/work/NewWorkItemMenu';
import QueueInsightCards from '../components/work/QueueInsightCards';
import WorkLayoutSwitch from '../components/work/WorkLayoutSwitch';
import WorkListPagination from '../components/work/WorkListPagination';
import {
  columnItemsForState,
  neighboursForDrop,
  orderedItemKeys,
  previousAfter,
  previousBefore,
  sortItemsByColumn,
} from '../components/work/kanban-rank';
import { BrandEmpty, ListEmpty, LoadingRows, PageTitle, WorkPageError } from '../components/work/page-states';
import {
  AssigneesCell,
  DateRangeCell,
  LabelsCell,
  PriorityLabel,
  StateLabel,
  TypeLabel,
  SubtypeLabel,
  SubtaskHint,
  WorkItemTitleLink,
  WorkKeyCell,
} from '../components/work/table-cells';
import ColumnsMenu from '../components/work/ColumnsMenu';
import { OPTIONAL_COLUMNS, useColumnVisibility } from '../components/work/use-column-visibility';
import { useProjectDecorations } from '../components/work/use-project-decorations';
import { ApiError, apiFetch, isProjectMembershipError, v1Path } from '../lib/api';
import {
  BACKLOG_DEFAULT_STATE_GROUPS,
  STATE_GROUPS_ANY,
  clearedWorkListSearchParams,
  filtersForLayoutSwitch,
  filtersToSearchParams,
  searchParamsToFilters,
  type DisplaySpec,
  type FilterSpec,
} from '../lib/filters';
import { workQueueFromPathname } from '../lib/work-queues';
import { useOverlays } from '../components/work/overlay-context';
import {
  matchesQueueFocus,
  parseQueueFocus,
  startOfDay,
  summarizeAssigned,
  type QueueFocus,
} from '../lib/assigned-queue';
import { paginateItems, WORK_LIST_PAGE_SIZE } from '../lib/work-item-list';
import { defaultWorkListOrderBy, orderByForLayoutSwitch } from '../lib/work-list-sort';
import { isServiceRequestProject } from '../lib/service-requests';
import type { ProjectDto, WorkItemListDecorations } from '../lib/work-types';
import { ProgressMeter } from '../components/work/ProgressParts';

type WorkItemSlim = {
  id: string;
  key: string;
  title: string;
  type?: string;
  subtype?: string | null;
  state_id: string;
  priority: string;
  assignee_ids: string[];
  label_ids: string[];
  parent_id: string | null;
  sort_order: number;
  version: number;
  start_date: string | null;
  due_date: string | null;
  blocked_reason?: string | null;
} & WorkItemListDecorations;

type StateDto = {
  id: string;
  name: string;
  group: string;
  color: string;
  position?: number;
  is_default: boolean;
};

type MoveBody = {
  version: number;
  state_id: string;
  before_id?: string;
  after_id?: string;
};

function sortStatesByPosition(rows: StateDto[]): StateDto[] {
  return [...rows].sort((left, right) => (left.position ?? 0) - (right.position ?? 0));
}

type ListResponse = {
  items: WorkItemSlim[];
  page: { limit: number; offset: number; total: number };
};

export default function Backlog() {
  const { projectId } = useParams<{ projectId: string }>();
  const location = useLocation();
  const queue = workQueueFromPathname(location.pathname);
  const lockedType = queue?.type ?? null;
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState<WorkItemSlim[]>([]);
  const [states, setStates] = useState<StateDto[]>([]);
  const [total, setTotal] = useState(0);
  const [projectEmpty, setProjectEmpty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selection, setSelection] = useState<BulkSelection>(new Map());
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(WORK_LIST_PAGE_SIZE);
  const { toast, openCreateWorkItem } = useOverlays();
  const { labels, members, subtypes } = useProjectDecorations(projectId);
  const { visible, setColumn } = useColumnVisibility();
  const navigate = useNavigate();
  const [serviceRequestProject, setServiceRequestProject] = useState(false);

  useEffect(() => {
    if (serviceRequestProject) navigate('/service-requests', { replace: true });
  }, [navigate, serviceRequestProject]);

  useEffect(() => {
    setServiceRequestProject(false);
    if (!projectId) return;
    let cancelled = false;
    apiFetch<ProjectDto>(v1Path(`/projects/${projectId}`))
      .then((project) => {
        if (!cancelled) setServiceRequestProject(isServiceRequestProject(project));
      })
      .catch(() => {
        if (!cancelled) setServiceRequestProject(false);
      });
    return () => { cancelled = true; };
  }, [projectId]);

  const parsed = useMemo(
    () =>
      searchParamsToFilters(searchParams),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchParams, queue],
  );

  const filters: FilterSpec = lockedType
    ? { ...parsed.filters, types: [lockedType] }
    : parsed.filters;
  const isKanban = parsed.display.layout !== 'list';
  const display: DisplaySpec = {
    layout: isKanban ? 'kanban' : 'list',
    order_by: defaultWorkListOrderBy(isKanban ? 'kanban' : 'list', parsed.display.order_by),
    group_by: isKanban ? 'state' : parsed.display.group_by ?? null,
    show_sub_items: parsed.display.show_sub_items ?? false,
    show_bugs: parsed.display.show_bugs ?? false,
    show_empty_groups: parsed.display.show_empty_groups ?? true,
  };
  const canReorder = isKanban && (display.order_by ?? 'sort_order') === 'sort_order';
  const focus = parseQueueFocus(searchParams.get('focus'));
  const today = useMemo(() => startOfDay(new Date()), []);

  const listQueryKey = useMemo(() => {
    const params = new URLSearchParams(searchParams);
    params.delete('peek');
    return params.toString();
  }, [searchParams]);

  const stateById = useMemo(() => {
    const map = new Map<string, StateDto>();
    for (const s of states) map.set(s.id, s);
    return map;
  }, [states]);

  const load = useCallback(async (opts?: { silent?: boolean }) => {
    if (!projectId) return;
    // A silent refetch keeps the table mounted so row enter/exit animations can
    // run. Only filter changes and the first load show the skeleton.
    if (!opts?.silent) setLoading(true);
    setError(null);
    try {
      const params = filtersToSearchParams(filters, display);
      params.set('limit', '100');
      params.set('offset', '0');
      const qs = params.toString();
      const [list, stateRes, unfiltered] = await Promise.all([
        apiFetch<ListResponse>(`${v1Path(`/projects/${projectId}/work-items`)}?${qs}`),
        apiFetch<{ items: StateDto[] }>(v1Path(`/projects/${projectId}/states`)),
        apiFetch<ListResponse>(
          `${v1Path(`/projects/${projectId}/work-items`)}?limit=1&offset=0`,
        ),
      ]);
      setItems(list.items);
      setTotal(list.page.total);
      setStates(stateRes.items);
      setProjectEmpty(unfiltered.page.total === 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load backlog');
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, listQueryKey, lockedType]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    function onChanged() {
      void load({ silent: true });
    }
    window.addEventListener('bb:work-items-changed', onChanged);
    return () => window.removeEventListener('bb:work-items-changed', onChanged);
  }, [load]);

  useEffect(() => {
    setSelection((curr) => {
      const next = new Map<string, { id: string; version: number }>();
      for (const item of items) {
        if (curr.has(item.id)) next.set(item.id, { id: item.id, version: item.version });
      }
      return next;
    });
  }, [items]);

  function applyFilters(next: { filters: FilterSpec; display: DisplaySpec }) {
    const peek = searchParams.get('peek') ?? undefined;
    const nextFilters = lockedType ? { ...next.filters, types: [lockedType] } : next.filters;
    const params = filtersToSearchParams(nextFilters, next.display, { peek });
    // A missing param re-applies the backlog defaults. Persist the any-token
    // so Clear (and unchecking every group) actually stays unconstrained.
    if (next.filters.state_groups == null) {
      params.set('state_groups', STATE_GROUPS_ANY);
    }
    setSearchParams(params, { replace: true });
  }

  function clearFilters() {
    setSearchParams(
      clearedWorkListSearchParams({
        lockedType,
        display: {
          layout: isKanban ? 'kanban' : 'list',
          order_by: defaultWorkListOrderBy(isKanban ? 'kanban' : 'list'),
          group_by: isKanban ? 'state' : null,
        },
        peek: searchParams.get('peek'),
      }),
      { replace: true },
    );
  }

  function searchForLayout(next: 'list' | 'kanban'): string {
    const peek = searchParams.get('peek') ?? undefined;
    const listDefaults = queue?.defaultStateGroups ?? BACKLOG_DEFAULT_STATE_GROUPS;
    const nextFilters = filtersForLayoutSwitch(filters, next, listDefaults);
    const params = filtersToSearchParams(
      nextFilters,
      {
        ...display,
        layout: next,
        group_by: next === 'kanban' ? 'state' : null,
        order_by: orderByForLayoutSwitch(next, display.order_by),
      },
      { peek },
    );
    if (nextFilters.state_groups == null) {
      params.set('state_groups', STATE_GROUPS_ANY);
    }
    return params.toString();
  }

  async function commitMove(item: WorkItemSlim, destStateId: string, insertBeforeId: string | null) {
    if (!projectId) return;
    if (!canReorder) {
      toast('Reorder is only allowed when Sort is Manual.');
      return;
    }
    if (item.id === insertBeforeId && item.state_id === destStateId) return;

    const destColumn = columnItemsForState(items, destStateId);
    const neighbours = neighboursForDrop(destColumn, item.id, insertBeforeId);
    const samePlace =
      item.state_id === destStateId &&
      (neighbours.before_id ?? null) === previousBefore(destColumn, item.id) &&
      (neighbours.after_id ?? null) === previousAfter(destColumn, item.id);
    if (samePlace) return;

    const snapshot = items;
    const optimistic: WorkItemSlim = { ...item, state_id: destStateId };
    setItems((curr) => curr.map((row) => (row.id === item.id ? optimistic : row)));

    const body: MoveBody = {
      version: item.version,
      state_id: destStateId,
      ...neighbours,
    };

    try {
      const dto = await apiFetch<WorkItemSlim>(
        v1Path(`/projects/${projectId}/work-items/${item.id}/move`),
        { method: 'POST', body: JSON.stringify(body) },
      );
      setItems((curr) =>
        sortItemsByColumn(curr.map((row) => (row.id === dto.id ? { ...row, ...dto } : row))),
      );
      await load({ silent: true });
    } catch (err) {
      setItems(snapshot);
      if (err instanceof ApiError && err.status === 409) {
        toast('Someone else updated this card. Reloading.');
        await load({ silent: true });
        return;
      }
      toast(err instanceof Error ? err.message : 'Move failed');
    }
  }

  const summary = useMemo(() => summarizeAssigned(items, today), [items, today]);
  const focusedItems = useMemo(
    () => items.filter((item) => matchesQueueFocus(item, focus, today)),
    [focus, items, today],
  );
  const paged = useMemo(
    () => paginateItems(focusedItems, page, pageSize),
    [focusedItems, page, pageSize],
  );
  const columns = sortStatesByPosition(states);

  useEffect(() => {
    setPage(1);
  }, [listQueryKey, focus, pageSize]);

  function toggleFocus(next: QueueFocus) {
    const params = new URLSearchParams(searchParams);
    if (focus === next) params.delete('focus');
    else params.set('focus', next);
    setSearchParams(params, { replace: true });
  }

  if (!projectId) {
    return <div className="p-6 text-sm text-muted-foreground">Missing stream.</div>;
  }

  const showEmptyProject = !loading && projectEmpty;
  const showNoMatches = !loading && !projectEmpty && total === 0;
  const listRows = isKanban ? items : paged.slice;
  const selectedOnPage = listRows.filter((item) => selection.has(item.id)).length;
  const allOnPageSelected = listRows.length > 0 && selectedOnPage === listRows.length;
  const someOnPageSelected = selectedOnPage > 0 && !allOnPageSelected;
  const peekKeys = isKanban ? orderedItemKeys(columns, items) : focusedItems.map((item) => item.key);

  return (
    <AppShell projectId={projectId}>
      <div className="flex flex-1 flex-col gap-4">
        <PageTitle
          title={queue?.label ?? 'Tasks'}
          count={loading || error ? undefined : focus ? focusedItems.length : total}
          action={
            <div className="flex items-center gap-2">
              <WorkLayoutSwitch
                isKanban={isKanban}
                listSearch={searchForLayout('list')}
                boardSearch={searchForLayout('kanban')}
              />
              <NewWorkItemMenu projectId={projectId} label={queue?.newLabel ?? 'New item'} />
            </div>
          }
        />

        {isProjectMembershipError(error) || loading || error ? null : (
          <QueueInsightCards summary={summary} active={focus} onToggle={toggleFocus} />
        )}

        {isProjectMembershipError(error) ? null : (
          <>
            <FilterBar
              projectId={projectId}
              lockedType={lockedType}
              defaultStateGroups={
                isKanban ? undefined : queue?.defaultStateGroups ?? BACKLOG_DEFAULT_STATE_GROUPS
              }
              filters={filters}
              display={display}
              onChange={applyFilters}
              onClear={clearFilters}
              trailing={isKanban ? null : <ColumnsMenu visible={visible} onChange={setColumn} />}
            />

            {isKanban ? null : (
              <div className="flex justify-end">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => openCreateWorkItem(serviceRequestProject
                    ? { type: 'service_request' }
                    : { projectId, type: lockedType ?? undefined })}
                >
                  <Plus className="h-4 w-4" aria-hidden />
                  {queue?.newLabel ?? 'New item'}
                </Button>
              </div>
            )}
          </>
        )}

        <WorkPageError error={error} projectId={projectId} onJoined={() => void load()} />

        {error || !isKanban || canReorder ? null : (
          <Alert>
            <AlertTitle>Dragging disabled</AlertTitle>
            <AlertDescription>Switch Sort to Manual to rearrange cards.</AlertDescription>
          </Alert>
        )}

        {error ? null : loading ? (
          <LoadingRows
            columns={isKanban ? 4 : 2 + OPTIONAL_COLUMNS.filter((column) => visible[column]).length}
          />
        ) : showEmptyProject ? (
          <BrandEmpty title={queue ? `No ${queue.label.toLowerCase()}.` : 'Nothing in this stream.'} />
        ) : showNoMatches ? (
          <ListEmpty
            title="No matches"
            description="Nothing matches the current filters."
            action={
              <Button type="button" variant="outline" size="sm" onClick={clearFilters}>
                Clear filters
              </Button>
            }
          />
        ) : isKanban ? (
          <WorkKanban
            projectId={projectId}
            columns={columns}
            items={items}
            showEmptyGroups={display.show_empty_groups !== false}
            canReorder={canReorder}
            onMove={(item, destStateId, insertBeforeId) => {
              void commitMove(item, destStateId, insertBeforeId);
            }}
            onAddCard={(stateId) => openCreateWorkItem(serviceRequestProject
              ? { type: 'service_request' }
              : { projectId, stateId, type: lockedType ?? undefined })}
            renderSelect={(item) => (
              <div onPointerDown={(event) => event.stopPropagation()}>
                <Checkbox
                  className="rounded-full"
                  checked={selection.has(item.id)}
                  onCheckedChange={(value) => {
                    setSelection((curr) => {
                      const next = new Map(curr);
                      if (value) next.set(item.id, { id: item.id, version: item.version });
                      else next.delete(item.id);
                      return next;
                    });
                  }}
                  aria-label={`Select ${item.key}`}
                />
              </div>
            )}
            renderActions={(item) => (
              <div onPointerDown={(event) => event.stopPropagation()}>
                <ItemContextMenu
                  projectId={projectId}
                  workItemId={item.id}
                  workItemKey={item.key}
                  version={item.version}
                  states={states}
                  currentStateId={item.state_id}
                  blockedReason={item.blocked_reason ?? null}
                  itemType={item.type}
                  onChanged={() => void load({ silent: true })}
                  onRemoved={(id) => setItems((curr) => curr.filter((row) => row.id !== id))}
                >
                  <Button type="button" variant="ghost" size="icon" className="h-6 w-6" aria-label="Card actions">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </ItemContextMenu>
              </div>
            )}
          />
        ) : (
          <>
          <Table className="table-fixed">
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">
                  <Checkbox
                    checked={
                      allOnPageSelected ? true : someOnPageSelected ? 'indeterminate' : false
                    }
                    onCheckedChange={(value) => {
                      if (value) {
                        setSelection(
                          new Map(
                            listRows.map((item) => [item.id, { id: item.id, version: item.version }]),
                          ),
                        );
                      } else {
                        setSelection(new Map());
                      }
                    }}
                    aria-label={allOnPageSelected ? 'Deselect all' : 'Select all'}
                  />
                </TableHead>
                <TableHead className="w-24">Key</TableHead>
                <TableHead className="min-w-0 w-[22%]">Title</TableHead>
                {visible.state ? (
                  <TableHead className="hidden w-[9rem] md:table-cell">State</TableHead>
                ) : null}
                {visible.priority ? (
                  <TableHead className="hidden w-24 md:table-cell">Priority</TableHead>
                ) : null}
                {visible.dates ? (
                  <TableHead className="hidden w-36 md:table-cell">Dates</TableHead>
                ) : null}
                {visible.assignee ? (
                  <TableHead className="hidden w-40 md:table-cell">Assignee</TableHead>
                ) : null}
                {visible.labels ? (
                  <TableHead className="hidden w-40 md:table-cell">Labels</TableHead>
                ) : null}
                <TableHead className="w-8" />
              </TableRow>
            </TableHeader>
            <TableBody>
              <AnimatePresence initial={false}>
              {listRows.map((item) => {
                const state = stateById.get(item.state_id);
                const checked = selection.has(item.id);
                return (
                  <WorkItemTableRow key={item.id} workItemId={item.id}>
                    <TableCell>
                      <Checkbox
                        checked={checked}
                        onCheckedChange={(value) => {
                          setSelection((curr) => {
                            const next = new Map(curr);
                            if (value) next.set(item.id, { id: item.id, version: item.version });
                            else next.delete(item.id);
                            return next;
                          });
                        }}
                        aria-label={`Select ${item.key}`}
                      />
                    </TableCell>
                    <TableCell className="min-w-0">
                      <div className="flex items-center gap-2 whitespace-nowrap">
                        <WorkKeyCell
                          workItemId={item.id}
                          workItemKey={item.key}
                          projectId={projectId}
                        />
                        {lockedType || !item.type ? null : <TypeLabel type={item.type} />}
                      </div>
                      <SubtypeLabel subtype={item.subtype} subtypes={subtypes} className="block truncate" />
                    </TableCell>
                    <TableCell className="min-w-0">
                      <div className="truncate" title={item.title}>
                        <WorkItemTitleLink
                          workItemId={item.id}
                          workItemKey={item.key}
                          projectId={projectId}
                          title={item.title}
                        />
                      </div>
                      <BlockedBadge reason={item.blocked_reason} className="mt-1" />
                      <SubtaskHint
                        projectId={projectId}
                        parentId={item.parent_id}
                        parentKey={item.parent_id ? items.find((row) => row.id === item.parent_id)?.key : undefined}
                        childCount={
                          item.rollup
                            ? item.rollup.total + item.rollup.cancelled
                            : items.filter((row) => row.parent_id === item.id).length
                        }
                      />
                      {item.rollup && item.rollup.total > 0 ? (
                        <ProgressMeter counts={item.rollup} className="mt-1 max-w-48" />
                      ) : null}
                      <span className="mt-0.5 flex items-center gap-2 md:hidden">
                        {state ? <StateLabel name={state.name} color={state.color} group={state.group} blocked={Boolean(item.blocked_reason)} /> : null}
                        <PriorityLabel priority={item.priority} />
                      </span>
                    </TableCell>
                    {visible.state ? (
                      <TableCell className="hidden md:table-cell">
                        {state ? <StateLabel name={state.name} color={state.color} group={state.group} blocked={Boolean(item.blocked_reason)} /> : null}
                      </TableCell>
                    ) : null}
                    {visible.priority ? (
                      <TableCell className="hidden md:table-cell">
                        <PriorityLabel priority={item.priority} />
                      </TableCell>
                    ) : null}
                    {visible.dates ? (
                      <TableCell className="hidden md:table-cell">
                        <DateRangeCell start={item.start_date} due={item.due_date} done={state?.group === 'completed' || state?.group === 'cancelled'} />
                      </TableCell>
                    ) : null}
                    {visible.assignee ? (
                      <TableCell className="hidden md:table-cell">
                        <AssigneesCell assigneeIds={item.assignee_ids} members={members} />
                      </TableCell>
                    ) : null}
                    {visible.labels ? (
                      <TableCell className="hidden md:table-cell">
                        <LabelsCell labelIds={item.label_ids} labels={labels} />
                      </TableCell>
                    ) : null}
                    <TableCell>
                      <ItemContextMenu
                        projectId={projectId}
                        workItemId={item.id}
                        workItemKey={item.key}
                        version={item.version}
                        states={states}
                        currentStateId={item.state_id}
                        blockedReason={item.blocked_reason ?? null}
                        itemType={item.type}
                        onChanged={() => void load({ silent: true })}
                        onRemoved={(id) => setItems((curr) => curr.filter((row) => row.id !== id))}
                      >
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          aria-label="Row actions"
                        >
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </ItemContextMenu>
                    </TableCell>
                  </WorkItemTableRow>
                );
              })}
              </AnimatePresence>
            </TableBody>
          </Table>
          <WorkListPagination
            page={paged.page}
            pages={paged.pages}
            pageSize={pageSize}
            shown={paged.slice.length}
            total={focusedItems.length}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
          />
          </>
        )}

        <BulkBar
          projectId={projectId}
          selection={selection}
          onClear={() => setSelection(new Map())}
          onApplied={() => void load({ silent: true })}
        />
        <Peek projectId={projectId} itemKeys={peekKeys} />

      </div>
    </AppShell>
  );
}
